<template>
  <div>
    <!-- Content -->
    <div class="row">
        <div class="col-xl-12">
          <div class="card">
            <div class="card-header">
                  <h5 class="mb-0">{{ $t('superAdmin.tenants.list') }}</h5>
            </div>
            <div class="card-body">
              <!-- DataGrid -->
              <DataGrid
                :data="formattedTenants"
                :columns="columns"
                v-model:currentPage="currentPage"
                v-model:pageSize="pageSize"
                v-model:searchText="searchTerm"
                v-model:sortField="sortField"
                v-model:sortDirection="sortDirection"
                :loading="loading"
                :error="error"
                :empty-state-message="$t('superAdmin.tenants.noData') || 'No tenants found'"
                :search-placeholder="$t('superAdmin.tenants.searchPlaceholder')"
                :total-items="totalItems"
                :storage-key="'tenantManagement_columnWidths'"
                :show-refresh="true"
                :actions="{ view: true, edit: false }"
                @search="searchTenants"
                @pageSizeChange="handlePageSizeChange"
                @pageChange="changePage"
                @sort="handleSort"
                @refresh="loadTenants"
              >
                <!-- User Type Filter Slot -->
                <template #filters>
                  <select 
                    class="form-select form-select-sm" 
                    v-model="userTypeFilter"
                    @change="handleUserTypeFilterChange"
                    style="min-width: 180px;"
                  >
                    <option value="">{{ $t('superAdmin.tenants.filter.allUserTypes') || 'All User Types' }}</option>
                    <option value="98caf202-9d8e-b62f-01c1-39ff3e9f71a0">{{ $t('superAdmin.tenants.userType.lookupUser') || 'Buyer Tenant' }}</option>
                    <option value="B0CB311A-A4D0-65B4-DF1B-39FF3E9F716E">{{ $t('superAdmin.tenants.userType.supplierTenant') || 'Supplier Tenant' }}</option>
                   
                  </select>
                </template>

                <!-- Actions Dropdown Slot -->
                <template #actions="{ item }">
                        <div class="btn-group position-relative">
                          <button 
                            class="btn btn-sm btn-outline-secondary dropdown-toggle" 
                            type="button" 
                      :id="`dropdownMenuButton${item.id}`"
                      @click="toggleDropdown(item.id, $event)"
                          >
                            <i class="ph-duotone ph-dots-three-vertical"></i>
                          </button>
                          <ul 
                            class="dropdown-menu dropdown-menu-end" 
                      :class="{ 'show': openDropdown === item.id }"
                      :aria-labelledby="`dropdownMenuButton${item.id}`"
                          >
                      <!-- Show Lookup Names - only for tenants with lookups -->
                      <li v-if="item.hasLookupsRaw">
                        <a 
                          class="dropdown-item" 
                          href="#"
                          @click.prevent="executeAction(() => showLookupNamesModal(item))"
                        >
                          <i class="ph-duotone ph-folder me-2"></i>
                          {{ $t('superAdmin.tenants.columns.lookupNames') }}
                        </a>
                      </li>
                            <!-- Update Companies - only for buyer tenants -->
                      <li v-if="item.isLookupUser">
                              <a 
                                class="dropdown-item" 
                                href="#"
                          @click.prevent="executeAction(() => updateCompanies(item._original))"
                          :class="{ 'disabled': updatingCompanies === item.id }"
                              >
                          <span v-if="updatingCompanies === item.id" class="spinner-border spinner-border-sm me-2"></span>
                                <i v-else class="ph-duotone ph-buildings me-2"></i>
                                {{ $t('superAdmin.tenants.actions.updateCompanies') }}
                              </a>
                            </li>
                      <li v-if="item.hasLookupsRaw || item.isLookupUser"><hr class="dropdown-divider"></li>
                            <li>
                        <a class="dropdown-item" href="#" @click.prevent="executeAction(() => impersonateTenant(item._original))">
                                <i class="ph-duotone ph-user-switch me-2"></i>
                                {{ $t('superAdmin.tenants.actions.impersonate') }}
                              </a>
                            </li>
                            <li>
                        <a class="dropdown-item" href="#" @click.prevent="executeAction(() => editTenant(item))">
                                <i class="ph-duotone ph-pencil me-2"></i>
                                {{ $t('superAdmin.tenants.actions.edit') }}
                              </a>
                            </li>
                            <li><hr class="dropdown-divider"></li>
                            <li>
                        <a class="dropdown-item text-danger" href="#" @click.prevent="executeAction(() => confirmDeleteTenant(item))">
                                <i class="ph-duotone ph-trash me-2"></i>
                                {{ $t('superAdmin.tenants.actions.delete') }}
                              </a>
                            </li>
                          </ul>
                        </div>
                        </template>
              </DataGrid>
            </div>
          </div>
        </div>
      </div>

    <!-- Edit Tenant Modal -->
    <div
      class="modal fade"
      id="editTenantModal"
      tabindex="-1"
      ref="editTenantModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('superAdmin.tenants.editTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveTenant">
              <div class="mb-3">
                <label class="form-label">{{ $t('superAdmin.tenants.form.name') }}</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="editingTenant.name"
                  required
                />
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveTenant"
              :disabled="saving"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      class="modal fade"
      id="deleteTenantModal"
      tabindex="-1"
      ref="deleteTenantModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-danger">{{ $t('superAdmin.tenants.deleteTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <i class="ph-duotone ph-warning-circle me-2"></i>
              {{ $t('superAdmin.tenants.deleteWarning') }}
            </div>
            <p>{{ $t('superAdmin.tenants.deleteConfirm', { name: deletingTenant?.name }) }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteTenant"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t('common.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lookup Names Modal -->
    <div
      class="modal fade"
      id="lookupNamesModal"
      tabindex="-1"
      ref="lookupNamesModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('superAdmin.tenants.lookupNamesTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="text-muted mb-3">{{ $t('superAdmin.tenants.lookupNamesDescription', { tenantName: selectedTenant?.name }) }}</p>
            <div class="lookup-names-list">
              <div v-for="(group, groupName) in groupedLookupNames" :key="groupName" class="lookup-group mb-4">
                <h6 class="text-primary mb-2">
                  <i class="ph-duotone ph-folder me-2"></i>
                  {{ groupName || $t('superAdmin.tenants.ungrouped') }}
                </h6>
                <div class="lookup-group-items">
                  <div v-for="(lookupName, index) in group" :key="index" class="lookup-name-item">
                    {{ lookupName.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.actions.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import * as bootstrap from 'bootstrap';
import superAdminService from '@/services/superAdminService';
import authService from '@/services/authService';
import { useDateFormatter } from '@/composables/useDateFormatter';
import DataGrid from '@/components/DataGrid.vue';
import Swal from 'sweetalert2';

const { t } = useI18n();
const store = useStore();
const { formatDate, formatDateTime } = useDateFormatter();

// Custom datetime formatter with timezone handling
const formatDateTimeLocal = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse the UTC date and convert to local time
    const date = new Date(dateString);
    
    // Format to local date and time
    const localDate = date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const localTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    return `${localDate} ${localTime}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return formatDateTime(dateString);
  }
};

// Reactive data
const tenants = ref([]);
const searchTerm = ref('');
const userTypeFilter = ref('');
const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const updatingCompanies = ref(null);
const openDropdown = ref(null);
const editingTenant = ref({});
const deletingTenant = ref(null);
const selectedTenant = ref(null);
const error = ref(null);

// Pagination and Sorting
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const sortField = ref('');
const sortDirection = ref('asc');

// Modal refs
const editTenantModal = ref(null);
const deleteTenantModal = ref(null);
const lookupNamesModal = ref(null);

// Bootstrap modals
let editModal;
let deleteModal;
let lookupNamesModalInstance;

// Define columns for DataGrid
const columns = [
  { key: 'name', label: t('superAdmin.tenants.columns.name'), width: '12%', sortable: true }, // ✅ Backend destekliyor
  { key: 'tenantCode', label: t('superAdmin.tenants.columns.tenantCode'), width: '8%', sortable: true }, // ✅ Backend destekliyor
  { 
    key: 'userType', 
    label: t('superAdmin.tenants.columns.edition'), 
    width: '15%', 
    type: 'badge',
    sortable: false // ✅ Zaten false
  },
  { key: 'creationDate', label: t('superAdmin.tenants.columns.creationDate'), width: '9%', sortable: true }, // ✅ Backend destekliyor (creationTime)
  { key: 'lastModificationDate', label: t('superAdmin.tenants.columns.lastModificationDate'), width: '9%', sortable: true }, // ✅ Backend destekliyor (lastModificationTime)
  { key: 'userCount', label: t('superAdmin.tenants.columns.userCount'), width: '6%', sortable: false }, // ❌ FALSE YAPIN - Entity level değil
  { key: 'adminEmail', label: t('superAdmin.tenants.columns.adminEmail'), width: '12%', type: 'truncate', sortable: false }, // ❌ FALSE YAPIN - Entity level değil
  { 
    key: 'hasLookups', 
    label: t('superAdmin.tenants.columns.hasLookups'), 
    width: '7%', 
    type: 'badge',
    sortable: false, // ✅ Zaten false
    badgeMap: {
      'Yes': 'badge bg-success',
      'No': 'badge bg-secondary'
    }
  },
  { key: 'lookupNames', label: t('superAdmin.tenants.columns.lookupNames'), width: '12%', type: 'truncate', sortable: false } // ✅ Zaten false
];

// Format tenants data for DataGrid
const formattedTenants = computed(() => {
  return tenants.value.map(tenant => {
    const isLookupUser = tenant.editionId && tenant.editionId.toLowerCase() === '98caf202-9d8e-b62f-01c1-39ff3e9f71a0';
    
    let userTypeDisplay = isLookupUser ? t('superAdmin.tenants.userType.lookupUser') : t('superAdmin.tenants.userType.normalUser');
    let userTypeColor = isLookupUser ? '#17a2b8' : '#6c757d';
    let userTypeSubtitle = '';
    
    if (isLookupUser && tenant.lastCompaniesFetchDate) {
      userTypeSubtitle = formatDateTimeLocal(tenant.lastCompaniesFetchDate);
    } else if (isLookupUser) {
      userTypeSubtitle = t('superAdmin.tenants.neverFetched');
    }
    
    let lookupNamesDisplay = '-';
    if (tenant.hasLookups && tenant.lookupNames && tenant.lookupNames.length > 0) {
      const firstTwo = tenant.lookupNames.slice(0, 2).join(', ');
      const remainingCount = tenant.lookupNames.length > 2 ? tenant.lookupNames.length - 2 : 0;
      lookupNamesDisplay = remainingCount > 0 ? `${firstTwo} +${remainingCount}` : firstTwo;
    }
    
    return {
      id: tenant.id,
      name: tenant.name,
      tenantCode: tenant.tenantCode,
      userType: userTypeDisplay,
      userTypeSubtitle: userTypeSubtitle,
      userTypeColor: userTypeColor,
      creationDate: formatDate(tenant.creationTime),
      lastModificationDate: tenant.lastModificationTime ? formatDate(tenant.lastModificationTime) : '-',
      userCount: tenant.userCount,
      adminEmail: tenant.adminEmail,
      hasLookups: tenant.hasLookups ? 'Yes' : 'No',
      hasLookupsRaw: tenant.hasLookups,
      lookupNames: lookupNamesDisplay,
      lookupNamesRaw: tenant.lookupNames,
      isLookupUser: isLookupUser,
      // Keep original data for actions
      _original: tenant
    };
  });
});

onMounted(() => {
  editModal = new Modal(editTenantModal.value);
  deleteModal = new Modal(deleteTenantModal.value);
  lookupNamesModalInstance = new Modal(lookupNamesModal.value);
  
  // Initialize Bootstrap tooltips
  import('bootstrap').then(({ Tooltip }) => {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl));
  });
  
  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.btn-group')) {
      openDropdown.value = null;
    }
  });
  
  loadTenants();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', (e) => {
    if (!e.target.closest('.btn-group')) {
      openDropdown.value = null;
    }
  });
});


// Methods
const loadTenants = async () => {
  loading.value = true;
  try {
    const options = {
      searchText: searchTerm.value,
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    // Add sorting if set
    if (sortField.value) {
      options.sorting = `${sortField.value} ${sortDirection.value}`;
    }
    
    // Add editionId filter if user type is selected
    if (userTypeFilter.value && userTypeFilter.value !== 'normal') {
      options.editionId = userTypeFilter.value;
    } else if (userTypeFilter.value === 'normal') {
      // For normal users, exclude lookup user editionId
      options.excludeEditionId = '98caf202-9d8e-b62f-01c1-39ff3e9f71a0';
    }
    
    const response = await superAdminService.getTenants(options);
    if (Array.isArray(response)) {
      tenants.value = response;
      totalItems.value = response.length;
    } else if (response?.items) {
      tenants.value = response.items;
      // Always use API's totalCount/rowCount for pagination
      totalItems.value = response.totalCount || response.rowCount || response.items.length;
    } else {
      tenants.value = [];
      totalItems.value = 0;
    }
  } catch (err) {
    console.error('Error loading tenants:', err);
    error.value = err.response?.data?.error?.message || t('superAdmin.tenants.errors.loadFailed');
    tenants.value = [];
    totalItems.value = 0;
  } finally {
    loading.value = false;
  }
};

const searchTenants = () => {
  currentPage.value = 1;
  loadTenants();
};

const handleUserTypeFilterChange = () => {
  currentPage.value = 1;
  loadTenants();
};

const handleSort = () => {
  currentPage.value = 1;
  loadTenants();
};

// Custom action handlers

const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value) || 1);

const getVisiblePages = () => {
  const maxVisible = 5;
  const pages = [];
  
  if (totalPages.value <= maxVisible) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages.value, start + maxVisible - 1);
    
    if (end === totalPages.value) {
      start = Math.max(1, totalPages.value - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }
  
  return pages;
};

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    loadTenants();
  }
};

const handlePageSizeChange = () => {
  currentPage.value = 1;
  loadTenants();
};

const editTenant = (tenant) => {
  // Handle both formatted (from DataGrid) and original tenant objects
  const originalTenant = tenant._original || tenant;
  editingTenant.value = { ...originalTenant };
  editModal.show();
};

const saveTenant = async () => {
  saving.value = true;
  try {
    await superAdminService.updateTenant(editingTenant.value);
    editModal.hide();
    store.dispatch('notify', {
      message: t('superAdmin.tenants.success.updated'),
      type: 'success'
    });
    loadTenants();
  } catch (error) {
    console.error('Error saving tenant:', error);
    store.dispatch('notify', {
      message: t('superAdmin.tenants.errors.updateFailed'),
      type: 'error'
    });
  } finally {
    saving.value = false;
  }
};

const confirmDeleteTenant = (tenant) => {
  // Handle both formatted (from DataGrid) and original tenant objects
  const originalTenant = tenant._original || tenant;
  deletingTenant.value = originalTenant;
  deleteModal.show();
};

const deleteTenant = async () => {
  deleting.value = true;
  try {
    await superAdminService.deleteTenant(deletingTenant.value.id);
    deleteModal.hide();
    store.dispatch('notify', {
      message: t('superAdmin.tenants.success.deleted'),
      type: 'success'
    });
    loadTenants();
  } catch (error) {
    console.error('Error deleting tenant:', error);
    store.dispatch('notify', {
      message: t('superAdmin.tenants.errors.deleteFailed'),
      type: 'error'
    });
  } finally {
    deleting.value = false;
  }
};

const showLookupNamesModal = (tenant) => {
  // Handle both formatted (from DataGrid) and original tenant objects
  const originalTenant = tenant._original || tenant;
  selectedTenant.value = originalTenant;
  lookupNamesModalInstance.show();
};

// Helper function to parse "Name (Group)" format using string methods to prevent ReDoS
const parseLookupName = (lookupName) => {
  const lastOpenParen = lookupName.lastIndexOf('(');
  const lastCloseParen = lookupName.lastIndexOf(')');
  
  const hasValidParentheses = lastOpenParen !== -1 && 
                              lastCloseParen !== -1 && 
                              lastCloseParen > lastOpenParen && 
                              lastCloseParen === lookupName.length - 1;
  
  if (!hasValidParentheses) {
    return null;
  }
  
  const name = lookupName.substring(0, lastOpenParen).trim();
  const group = lookupName.substring(lastOpenParen + 1, lastCloseParen).trim();
  
  if (!name || !group) {
    return null;
  }
  
  return { name, group };
};

// Helper function to add item to a group
const addToGroup = (groups, groupName, item) => {
  if (!groups[groupName]) {
    groups[groupName] = [];
  }
  groups[groupName].push(item);
};

// Group lookup names by the value in parentheses
const groupedLookupNames = computed(() => {
  if (!selectedTenant.value?.lookupNames || selectedTenant.value.lookupNames.length === 0) {
    return {};
  }
  
  const groups = {};
  
  selectedTenant.value.lookupNames.forEach(lookupName => {
    const parsed = parseLookupName(lookupName);
    
    if (parsed) {
      addToGroup(groups, parsed.group, { name: parsed.name, fullName: lookupName });
    } else {
      addToGroup(groups, 'Ungrouped', { name: lookupName, fullName: lookupName });
    }
  });
  
  return groups;
});

const toggleDropdown = (tenantId, event) => {
  if (openDropdown.value === tenantId) {
    openDropdown.value = null;
  } else {
    openDropdown.value = tenantId;
    
    // Position the dropdown menu using fixed positioning
    nextTick(() => {
      const button = event?.currentTarget || document.getElementById(`dropdownMenuButton${tenantId}`);
      const dropdown = button?.nextElementSibling;
      
      if (button && dropdown) {
        const buttonRect = button.getBoundingClientRect();
        dropdown.style.top = `${buttonRect.bottom + 4}px`;
        dropdown.style.right = `${window.innerWidth - buttonRect.right}px`;
        dropdown.style.left = 'auto';
      }
    });
  }
};

const executeAction = (action) => {
  console.log('Executing action');
  openDropdown.value = null; // Close dropdown
  action(); // Execute the action
};

const updateCompanies = async (tenant) => {
  const result = await Swal.fire({
    title: t('superAdmin.tenants.actions.updateCompanies'),
    text: t('superAdmin.tenants.updateCompaniesConfirm', { name: tenant.name }),
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d',
    confirmButtonText: t('common.actions.confirm'),
    cancelButtonText: t('common.cancel'),
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-outline-secondary'
    },
    buttonsStyling: false
  });
  
  if (!result.isConfirmed) return;
  
  updatingCompanies.value = tenant.id;
  try {
    await superAdminService.updateCompanies(tenant.id);
    store.dispatch('notify', {
      message: t('superAdmin.tenants.success.companiesUpdated'),
      type: 'success'
    });
    // Refresh the tenant list to show updated LastCompaniesFetchDate
    loadTenants();
  } catch (error) {
    console.error('Error updating companies:', error);
    store.dispatch('notify', {
      message: t('superAdmin.tenants.errors.updateCompaniesFailed'),
      type: 'error'
    });
  } finally {
    updatingCompanies.value = null;
  }
};

const impersonateTenant = async (tenant) => {
  const result = await Swal.fire({
    title: t('superAdmin.tenants.actions.impersonate'),
    text: t('superAdmin.tenants.impersonateConfirm', { name: tenant.name }),
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#ff6b35',
    cancelButtonColor: '#6c757d',
    confirmButtonText: t('superAdmin.tenants.actions.impersonate'),
    cancelButtonText: t('common.cancel'),
    customClass: {
      confirmButton: 'btn btn-warning',
      cancelButton: 'btn btn-outline-secondary'
    },
    buttonsStyling: false
  });
  
  if (!result.isConfirmed) return;
  
  try {
    const response = await authService.impersonateTenant(tenant.id, tenant.name);
    
    // Update user in store
    store.commit('auth/SET_USER', response.user);
    store.commit('auth/SET_TOKEN', response.token);
    
    store.dispatch('notify', {
      message: t('superAdmin.tenants.success.impersonationStarted', { name: tenant.name }),
      type: 'success'
    });

    // Redirect to dashboard after impersonation
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  } catch (error) {
    console.error('Error impersonating tenant:', error);
    store.dispatch('notify', {
      message: t('superAdmin.tenants.errors.impersonationFailed'),
      type: 'error'
    });
  }
};
</script>

<style scoped>
/* Table column widths optimization */
.col-name { width: 12%; min-width: 100px; }
.col-code { width: 8%; min-width: 80px; }
.col-type { width: 15%; min-width: 130px; }
.col-date { width: 9%; min-width: 90px; }
.col-count { width: 6%; min-width: 50px; }
.col-email { width: 12%; min-width: 120px; }
.col-lookups { width: 7%; min-width: 70px; }
.col-lookup-names { width: 12%; min-width: 100px; }
.col-actions { width: 6%; min-width: 60px; }

.lookup-names {
  max-width: 150px;
}

.lookup-item {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s;
}

.lookup-item:hover {
  background-color: #f0f0f0;
  border-radius: 4px;
}

.lookup-more {
  font-size: 0.75rem;
  color: #999;
  font-style: italic;
}

.table td {
  vertical-align: middle;
  font-size: 0.875rem;
}

.table th {
  font-size: 0.875rem;
  font-weight: 600;
}

/* Compact user type display */
.badge.d-block {
  margin-bottom: 4px;
}

/* Card and table overflow fix for dropdowns */
.card-body {
  overflow: visible;
}

.table-responsive {
  overflow: visible !important;
}

.card {
  overflow: visible;
}

.table {
  overflow: visible;
}

tbody {
  overflow: visible;
}

tr {
  overflow: visible;
}

td {
  overflow: visible;
}

/* Ensure actions column doesn't clip dropdown */
.col-actions {
  overflow: visible !important;
  position: relative;
}

/* Dropdown actions styling */
.btn-group {
  position: relative;
}

.dropdown-toggle::after {
  display: none; /* Hide default dropdown arrow */
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  min-width: 200px;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  background-color: white;
  position: fixed !important;
  z-index: 9999 !important;
  margin-top: 4px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 0.875rem;
  white-space: nowrap;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item:disabled {
  opacity: 0.6;
  pointer-events: none;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

.lookup-names-list {
  max-height: 400px;
  overflow-y: auto;
}

.lookup-group {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
}

.lookup-group:last-child {
  border-bottom: none;
  margin-bottom: 0 !important;
}

.lookup-group h6 {
  font-size: 0.95rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.75rem;
}

.lookup-group-items {
  padding-left: 1rem;
}

.lookup-name-item {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.lookup-name-item:last-child {
  border-bottom: none;
}

.lookup-name-item:hover {
  background-color: #f8f9fa;
}

/* Responsive improvements */
@media (max-width: 1600px) {
  .col-email { width: 10%; }
  .col-lookup-names { width: 10%; }
  .col-name { width: 10%; }
}

@media (max-width: 1400px) {
  .col-email { width: 8%; }
  .col-lookup-names { width: 8%; }
  .col-name { width: 8%; }
  .col-date { width: 8%; }
}
</style> 